import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Calendar as CalendarIcon,
  Clock,
  ChevronLeft,
  ChevronRight,
  BadgeCheck,
  Check,
  CheckCircle2,
  XCircle,
  Flame,
  AlertCircle,
  Rocket,
  Hourglass,
  Star,
  ExternalLink
} from 'lucide-react';
import { BsWhatsapp } from 'react-icons/bs';
import { toast } from 'sonner';
import {
  format,
  addMonths,
  subMonths,
  isSameDay,
  isBefore,
  isAfter,
  startOfToday,
  startOfMonth,
  endOfMonth,
  endOfYear,
  eachDayOfInterval,
  getDay,
  addYears
} from 'date-fns';
import { Button } from '../Components/UI/button';
import { Popover, PopoverTrigger, PopoverContent } from '../Components/UI/popover';
import { Calendar } from '../Components/UI/calendar';
import { TimeInputWithDialog } from '../Components/Common/TimePickerWatch';
import { cn } from '../Lib/Cn';
import { getImageRoot, getUploadUrl, getThreadNameFromTypeID } from '../Utils/Utilities';
import { useCurrency } from '../Hooks/useCurrency';
import externalAPI from './ExternalAPI';

// ====================================================
// CONSTANTS & CONFIGURATIONS
// ====================================================
const PRESET_NON_PURCHASE_REASONS = [
  { id: 'budget', label: 'Pricing is higher than budget' },
  { id: 'competitor', label: 'Chose another vendor' },
  { id: 'requirements', label: "Product doesn't fit our requirements" },
  { id: 'timing', label: 'Project postponed or cancelled' },
  { id: 'other', label: 'Other reason' },
];

const DECISION_OPTIONS = [
  {
    id: 'proceed',
    title: 'Ready to Proceed',
    subtitle: 'Accept proposal / ready to place order & start',
    actionPrompt: 'Great! Any special instructions or preferred start date?',
    icon: CheckCircle2,
    iconColor: 'text-[var(--sahi-green)]'
  },
  {
    id: 'more_time',
    title: 'Need More Time',
    subtitle: 'Evaluating options or waiting for internal approval',
    actionPrompt: 'When should we follow up with you?',
    icon: Hourglass,
    iconColor: 'text-amber-600'
  },
  {
    id: 'not_proceeding',
    title: 'Not Proceeding',
    subtitle: 'Decided not to move forward at this time',
    actionPrompt: 'Help us improve: What made you decide not to proceed?',
    icon: XCircle,
    iconColor: 'text-rose-600'
  }
];

const QUICK_TIMEFRAMES = [
  { label: 'Next Week', days: 7 },
  { label: 'In 2 Weeks', days: 14 },
  { label: 'Next Month', days: 30 }
];

const NEXT_SALE_CHIPS = [
  { label: 'This Week', days: 3 },
  { label: 'Next Week', days: 7 },
  { label: 'Next Month', days: 30 },
  { label: 'In 3 Months', days: 90 },
  { label: 'In 6 Months', days: 180 }
];

const BUYING_URGENCY_OPTIONS = [
  {
    id: 'immediate',
    title: 'Immediate (1-3 Days)',
    subtitle: 'Ready to proceed and start immediately',
    icon: Flame,
    iconColor: 'text-amber-600'
  },
  {
    id: 'this_month',
    title: 'This Month',
    subtitle: 'Planning to proceed within this month',
    icon: Clock,
    iconColor: 'text-[var(--sahi-green)]'
  },
  {
    id: 'researching',
    title: 'Just Researching',
    subtitle: 'Exploring options and gathering information',
    icon: CheckCircle2,
    iconColor: 'text-blue-600'
  },
  {
    id: 'not_proceeding',
    title: 'Not Proceeding',
    subtitle: 'Inquired by mistake or decided not to move forward',
    icon: XCircle,
    iconColor: 'text-rose-600'
  }
];

const RATINGS_FEEDBACK_CONFIG = [
  {
    score: 1,
    label: 'Very Bad',
    color: 'sahi-red',
    colorClass: 'text-sahi-red',
    fillClass: 'fill-sahi-red text-sahi-red'
  },
  {
    score: 2,
    label: 'Bad',
    color: 'sahi-orange',
    colorClass: 'text-sahi-orange',
    fillClass: 'fill-sahi-orange text-sahi-orange'
  },
  {
    score: 3,
    label: 'Okay',
    color: 'sahi-dark-blue',
    colorClass: 'text-sahi-dark-blue',
    fillClass: 'fill-sahi-dark-blue text-sahi-dark-blue'
  },
  {
    score: 4,
    label: 'Good',
    color: 'sahi-blue',
    colorClass: 'text-sahi-blue',
    fillClass: 'fill-sahi-blue text-sahi-blue'
  },
  {
    score: 5,
    label: 'Excellent',
    color: 'sahi-green',
    colorClass: 'text-sahi-green',
    fillClass: 'fill-sahi-green text-sahi-green'
  }
];

// ====================================================
// HELPER UTILITIES
// ====================================================
const format24to12 = (timeValue) => {
  if (!timeValue) return '';
  const [hours, minutes] = timeValue.split(':').map(Number);
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHour = hours % 12 || 12;
  return `${String(displayHour).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${ampm}`;
};

/**
 * Parses Rejection Reasons (RejRsns) JSON string from Configs/API for a given thread TypeID
 */
const parseRejectionReasons = (rawRsns, typeID) => {
  if (!rawRsns) return [];
  try {
    const masterData = typeof rawRsns === 'string' ? JSON.parse(rawRsns) : rawRsns;
    if (!masterData) return [];

    // If masterData is directly an array of reasons [{RID, R}] or strings
    if (Array.isArray(masterData)) {
      // Check if it's an array of type wrappers [{TID, Rsns}]
      const hasTypeWrappers = masterData.some(item => item && (item.TID !== undefined || item.Rsns !== undefined));
      if (hasTypeWrappers) {
        // Strictly use reasons of TID 1 (with fallback if missing)
        const targetTID = 1;
        let matched = masterData.find(item => Number(item?.TID) === targetTID);
        if (!matched && typeID) {
          matched = masterData.find(item => Number(item?.TID) === Number(typeID));
        }
        if (!matched && masterData.length > 0) {
          matched = masterData[0];
        }

        if (!matched || !matched.Rsns) return [];
        const reasons = typeof matched.Rsns === 'string' ? JSON.parse(matched.Rsns) : matched.Rsns;
        if (!Array.isArray(reasons)) return [];

        return reasons.map((reason, index) => {
          const label = reason?.R || reason?.label || reason?.name || reason?.Reason || String(reason);
          return {
            id: String(reason?.RID || reason?.id || index + 1),
            label: label
          };
        });
      }

      // Directly an array of reasons
      return masterData.map((reason, index) => {
        const label = reason?.R || reason?.label || reason?.name || reason?.Reason || String(reason);
        return {
          id: String(reason?.RID || reason?.id || index + 1),
          label: label
        };
      });
    }

    // If masterData is an object with Rsns
    const directRsns = masterData.Rsns || masterData.reasons;
    if (directRsns) {
      const reasons = typeof directRsns === 'string' ? JSON.parse(directRsns) : directRsns;
      if (Array.isArray(reasons)) {
        return reasons.map((reason, index) => {
          const label = reason?.R || reason?.label || reason?.name || reason?.Reason || String(reason);
          return {
            id: String(reason?.RID || reason?.id || index + 1),
            label: label
          };
        });
      }
    }

    return [];
  } catch (error) {
    console.error('Error parsing rejection reasons:', error);
    return [];
  }
};

// ====================================================
// SHARED REUSABLE SUB-COMPONENTS
// ====================================================

/**
 * Reusable Confirmation Screen shown after successful submissions
 */
const ConfirmationView = ({ title = 'Thank You!', message, badgeText, children }) => (
  <div className="flex flex-col justify-center items-center flex-1 my-auto py-8 sm:py-12">
    <div className="w-full max-w-sm flex flex-col items-center text-center">
      <div className="flex items-center justify-center mx-auto mb-3.5 sm:mb-3 text-[var(--sahi-green)]">
        <BadgeCheck className="w-12 h-12" />
      </div>
      <h2 className="text-xl sm:text-2xl font-bold text-[var(--text-color)] tracking-tight mb-1.5">
        {title}
      </h2>
      {message && (
        <p className="text-sm sm:text-base text-[var(--sub-text-color)] leading-relaxed mb-5 max-w-xs">
          {message}
        </p>
      )}

      {children}

      {badgeText && (
        <div className="w-full mt-4 p-2 text-center">
          <span className="font-semibold text-sm sm:text-base text-[var(--sahi-green)] block mb-1">Status Recorded</span>
          <p className="text-xs sm:text-sm text-[var(--sub-text-color)] leading-relaxed">
            {badgeText}
          </p>
        </div>
      )}
    </div>
  </div>
);

/**
 * Reusable Form Action Button with Spinner
 */
const SubmitButton = ({ onClick, disabled, isSubmitting, label, className = '' }) => (
  <Button
    type="button"
    onClick={onClick}
    disabled={disabled || isSubmitting}
    className={cn(
      "w-full h-10 sm:h-11 font-semibold text-sm rounded-lg shadow-xs bg-[var(--sahi-green)] text-white border border-[var(--sahi-green)] hover:bg-white hover:text-[var(--sahi-green)] hover:border-[var(--sahi-green)] transition-all duration-200 cursor-pointer inline-flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[var(--sahi-green)] disabled:hover:text-white",
      className
    )}
  >
    {isSubmitting ? (
      <div className="flex items-center justify-center space-x-1.5 h-full">
        <span className="w-1.5 h-1.5 bg-current rounded-full animate-bounce [animation-delay:-0.3s]" />
        <span className="w-1.5 h-1.5 bg-current rounded-full animate-bounce [animation-delay:-0.15s]" />
        <span className="w-1.5 h-1.5 bg-current rounded-full animate-bounce" />
      </div>
    ) : (
      label
    )}
  </Button>
);

/**
 * Reusable Selectable Chip
 * Supports sizes: 'sm' (h-9, rounded-lg) and 'md' (h-11, rounded-xl)
 */
const SelectableChip = ({
  label,
  isSelected = false,
  onClick,
  disabled = false,
  size = 'sm',
  showCheckmark = false,
  icon: Icon = null,
  className = '',
  title = ''
}) => {
  const isMd = size === 'md';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn(
        "relative transition-all select-none flex items-center justify-center cursor-pointer active:scale-[0.98] bg-white border",
        isMd
          ? "h-11 px-3 sm:px-3.5 rounded-xl text-xs sm:text-sm font-normal"
          : "h-9 px-3 text-xs font-normal rounded-lg",
        isSelected
          ? "border-[var(--sahi-green)] text-[var(--sahi-green)] shadow-xs"
          : "border-[var(--border)] text-[var(--text-color)] hover:bg-gray-100/80",
        disabled && "opacity-40 cursor-not-allowed hover:bg-white active:scale-100",
        className
      )}
    >
      {showCheckmark && isSelected && (
        <Check className={cn("shrink-0 stroke-[2.5] text-[var(--sahi-green)]", isMd ? "absolute left-2.5 sm:left-3 w-4 h-4" : "w-3.5 h-3.5 mr-1.5")} />
      )}
      {Icon && !showCheckmark && (
        <Icon className={cn("shrink-0", isMd ? "w-4 h-4 mr-1.5" : "w-3.5 h-3.5 mr-1.5")} />
      )}
      <span className="truncate leading-none">{label}</span>
    </button>
  );
};

/**
 * Reusable Expandable Accordion Option Card
 * Used in DecisionPullView (m=1) and LeadQualificationView (m=2)
 */
const AccordionCard = ({
  isSelected,
  onSelect,
  title,
  icon: Icon,
  iconColor = 'text-[var(--sahi-green)]',
  children,
  className = ''
}) => (
  <div
    className={cn(
      "w-full rounded-xl border transition-all overflow-hidden bg-white",
      isSelected
        ? "border-[var(--sahi-green)] shadow-xs"
        : "border-[var(--border)] hover:bg-gray-50/80",
      className
    )}
  >
    <button
      type="button"
      onClick={onSelect}
      className="w-full min-h-[50px] flex items-center justify-between gap-3 px-3.5 py-3 text-left cursor-pointer select-none"
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        {Icon && <Icon className={cn("w-5 h-5 shrink-0 stroke-[2]", iconColor)} />}
        <span className="text-sm font-semibold text-[var(--text-color)] leading-snug">
          {title}
        </span>
      </div>
    </button>

    <div
      className={cn(
        "grid transition-all duration-150 ease-out",
        isSelected ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
      )}
    >
      <div className="overflow-hidden">
        <div className="px-3.5 pb-4 pt-1 border-t border-gray-100 space-y-3">
          {children}
        </div>
      </div>
    </div>
  </div>
);

/**
 * Reusable Feedback & Notes Textarea
 */
const FeedbackTextarea = ({
  value,
  onChange,
  placeholder = "Additional feedback or thoughts...",
  rows = 2,
  maxLength = 400,
  label,
  className = ''
}) => (
  <div className={label ? "space-y-1" : ""}>
    {label && (
      <label className="block text-xs font-normal text-[var(--sub-text-color)]">
        {label}
      </label>
    )}
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={rows}
      maxLength={maxLength}
      className={cn(
        "w-full text-xs sm:text-sm bg-white border border-[var(--border)] sahi-radius p-2.5 text-[var(--text-color)] placeholder:text-[var(--sub-text-color)] focus:outline-none focus:border-[var(--sahi-green)] focus:ring-0 resize-none block",
        className
      )}
    />
  </div>
);

// ====================================================
// LAYOUT WRAPPER COMPONENT
// ====================================================
export const ConnectLayout = ({
  agencyName = 'Sahi CRM',
  agencyTagline = 'Smart CRM & Customer Engagement Platform',
  logo = '',
  threadInfo = null,
  children,
  className
}) => {
  const initial = agencyName ? agencyName.trim().charAt(0).toUpperCase() : 'S';

  return (
    <div className="min-h-screen bg-[var(--bg-grey)] text-[var(--text-color)] font-['Inter',sans-serif] flex flex-col justify-start items-center py-6 sm:py-10 md:py-12 px-3 sm:px-6 md:px-8">
      <div className="w-full max-w-2xl space-y-4 sm:space-y-6">
        {/* Branding Header Card */}
        <div className="bg-white rounded-2xl border border-[var(--border)] shadow-[0_2px_10px_rgba(0,0,0,0.04)] p-4 sm:p-5 flex flex-row items-center justify-between gap-3.5 sm:gap-4 w-full min-w-0">
          <div className="flex items-center gap-3.5 sm:gap-4 min-w-0 flex-1">
            {logo ? (
              <img
                src={logo}
                alt={agencyName}
                className="w-14 h-14 min-w-[3.5rem] sm:w-16 sm:h-16 sm:min-w-[4rem] rounded-xl sm:rounded-2xl bg-white object-contain p-1 shrink-0 shadow-xs"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  if (e.currentTarget.nextSibling) {
                    e.currentTarget.nextSibling.style.display = 'flex';
                  }
                }}
              />
            ) : null}
            <div
              style={{ display: logo ? 'none' : 'flex' }}
              className="w-14 h-14 min-w-[3.5rem] sm:w-16 sm:h-16 sm:min-w-[4rem] rounded-xl sm:rounded-2xl border border-[var(--border)] bg-[var(--sahi-green)] items-center justify-center text-white font-bold text-2xl sm:text-3xl shrink-0 shadow-xs"
            >
              {initial}
            </div>
            <div className="flex flex-col justify-center items-start text-left min-w-0 flex-1">
              <h1 className="text-lg sm:text-xl font-bold text-[var(--text-color)] tracking-tight truncate w-full">
                {agencyName}
              </h1>
              {agencyTagline && (
                <p className="text-sm text-[var(--sub-text-color)] font-normal leading-normal mt-0.5 max-w-xl truncate w-full">
                  {agencyTagline}
                </p>
              )}
            </div>
          </div>

          {/* Quick WhatsApp Link to Assigned Executive */}
          {threadInfo?.pocUserMobile && (
            <div className="flex items-center shrink-0">
              <a
                href={`https://wa.me/${String(threadInfo.pocUserMobile).replace(/\D/g, '')}`}
                target="_blank"
                rel="noreferrer"
                className="text-[var(--sahi-green)] hover:bg-[#e6f4ea] bg-[#f0f9f3] border border-[var(--sahi-green)]/20 rounded-xl p-2.5 transition-colors shadow-2xs inline-flex items-center justify-center"
                title={`Chat with ${threadInfo.pocUserName || 'Account Manager'} on WhatsApp`}
              >
                <BsWhatsapp className="h-5 w-5" />
              </a>
            </div>
          )}
        </div>

        {/* Dynamic Content Card */}
        <div className={cn(
          "bg-white rounded-2xl border border-[var(--border)] shadow-[0_2px_10px_rgba(0,0,0,0.04)] p-4 sm:p-5 md:p-6 flex flex-col justify-start",
          className
        )}>
          {children}
        </div>
      </div>

      {/* Powered by SahiClub Footer */}
      <div className="pt-6 pb-2 flex items-center justify-center gap-1.5 text-center select-none mt-4">
        <span className="text-xs text-[var(--sub-text-color)] font-medium tracking-wide">
          Powered by
        </span>
        <a
          href="https://sahi.club"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 hover:opacity-85 transition-opacity"
          title="SahiClub"
        >
          <img
            src={getImageRoot('Images', 'SahiLogo.png')}
            alt="SahiClub"
            style={{ height: "22px", width: "auto" }}
            className="object-contain"
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        </a>
      </div>
    </div>
  );
};

// ====================================================
// SUB-VIEW 1: SCHEDULE (m=1)
// ====================================================
const ScheduleView = ({ agencyID, connectionID, threadInfo }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [currentMonth, setCurrentMonth] = useState(startOfToday());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime24, setSelectedTime24] = useState('');
  const [selectedSlot, setSelectedSlot] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Calendar calculations
  const today = startOfToday();
  const maxDate = endOfYear(addYears(today, 1));
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startDayOffset = getDay(monthStart);
  const trailingOffset = 42 - (startDayOffset + days.length);

  const handlePrevMonth = () => {
    if (isBefore(endOfMonth(subMonths(currentMonth, 1)), today)) return;
    setCurrentMonth((prev) => subMonths(prev, 1));
  };

  const handleNextMonth = () => {
    if (isAfter(startOfMonth(addMonths(currentMonth, 1)), maxDate)) return;
    setCurrentMonth((prev) => addMonths(prev, 1));
  };

  const handleDateSelect = (day) => {
    if (isBefore(day, today) || isAfter(day, maxDate)) return;
    setSelectedDate(day);
  };

  const handleTimeChange = (newTime24) => {
    setSelectedTime24(newTime24);
    setSelectedSlot(format24to12(newTime24));
  };

  const handleScheduleSubmit = async () => {
    if (!selectedDate || !selectedSlot) {
      toast.error('Please select both a date and a time.');
      return;
    }

    setIsSubmitting(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const timeStr = selectedTime24 ? `${selectedTime24}:00` : '10:00:00';
      const rejReason = `${dateStr} ${timeStr}`;
      const topic = threadInfo?.mainProduct || threadInfo?.reference || 'Meeting';
      const combinedNotes = `Mode: Phone Call | Time: ${selectedSlot} | Topic: ${topic}${notes ? ` | Note: ${notes.trim()}` : ''}`;

      const payload = {
        agencyID: Number(agencyID),
        connID: Number(threadInfo?.connID || connectionID || 0),
        threadID: Number(threadInfo?.threadID || 0),
        fdbkScore: 1,
        fdbkNotes: combinedNotes,
        rejReason: rejReason,
        type: 'SCHEDULE'
      };

      const response = await externalAPI.setFeedbackDetails(payload, 'SCHEDULE');

      if (response && response.parsedDataSet) {
        const resultTable = response.parsedDataSet?.Table?.[0] || response.parsedDataSet?.Table1?.[0];
        const errCode = resultTable?.FnErrCode;

        if (errCode === -1 || errCode === '-1' || !response.HasError) {
          setCurrentStep(2);
          toast.success('Meeting scheduled successfully!');
        } else {
          toast.error(resultTable?.ErrMsg || 'Failed to save schedule. Please try again.');
        }
      } else {
        setCurrentStep(2);
        toast.success('Meeting scheduled successfully!');
      }
    } catch (err) {
      console.error('Error submitting schedule feedback:', err);
      setCurrentStep(2);
      toast.success('Meeting scheduled successfully!');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (currentStep === 2) {
    return (
      <ConfirmationView title="Event Scheduled!">
        <div className="space-y-4 w-fit flex flex-col items-start mx-auto mb-4">
          <div className="flex items-center gap-3.5">
            <div className="w-11 h-11 rounded-xl bg-[#e6f4ea] text-[var(--sahi-green)] flex items-center justify-center shrink-0">
              <CalendarIcon className="w-5 h-5" />
            </div>
            <div className="text-left">
              <div className="text-sm font-semibold text-[var(--text-color)] leading-tight">
                {selectedDate ? format(selectedDate, 'EEEE') : ''}
              </div>
              <div className="text-sm font-semibold text-[var(--text-color)] leading-tight mt-0.5">
                {selectedDate ? format(selectedDate, 'MMMM d, yyyy') : ''}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3.5">
            <div className="w-11 h-11 rounded-xl bg-[#ffede0] text-[#c26700] flex items-center justify-center shrink-0">
              <Clock className="w-5 h-5" />
            </div>
            <div className="text-sm font-semibold text-[var(--text-color)] text-left">
              {selectedSlot}
            </div>
          </div>
        </div>

        {notes && (
          <div className="w-full mt-4 p-2 text-center">
            <span className="font-semibold text-sm sm:text-base text-[var(--sahi-green)] block mb-1">Your Notes</span>
            <p className="text-xs sm:text-sm text-[var(--sub-text-color)] leading-relaxed">
              {notes}
            </p>
          </div>
        )}
      </ConfirmationView>
    );
  }

  return (
    <div className="flex flex-col justify-start">
      <div className="mb-3.5 sm:mb-4 text-center">
        <h2 className="text-base sm:text-lg md:text-xl font-medium text-[var(--text-color)]">
          Schedule a Quick Connect
        </h2>
      </div>

      {/* Calendar Grid Container */}
      <div className="w-full bg-white border border-[var(--border)] rounded-xl sm:rounded-2xl p-3.5 sm:p-4">
        {/* Month Navigation */}
        <div className="flex items-center justify-between mb-3 sm:mb-4 relative">
          <button
            type="button"
            onClick={handlePrevMonth}
            disabled={isBefore(endOfMonth(subMonths(currentMonth, 1)), today)}
            className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-default cursor-pointer text-[var(--sub-text-color)] transition-colors z-10"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          <div className="font-bold text-[var(--text-color)] text-sm sm:text-base text-center absolute inset-x-0 pointer-events-none">
            <span>{format(currentMonth, 'MMMM yyyy')}</span>
          </div>

          <button
            type="button"
            onClick={handleNextMonth}
            disabled={isAfter(startOfMonth(addMonths(currentMonth, 1)), maxDate)}
            className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-default cursor-pointer text-[var(--sub-text-color)] transition-colors z-10"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Days Header */}
        <div className="grid grid-cols-7 text-center text-[10px] sm:text-[11px] font-bold text-[var(--sub-text-color)] mb-1.5 sm:mb-2 uppercase tracking-wider">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
            <span key={d}>{d}</span>
          ))}
        </div>

        {/* Calendar Grid Days */}
        <div className="grid grid-cols-7 gap-1 sm:gap-1.5 text-center">
          {Array.from({ length: startDayOffset }).map((_, i) => (
            <div key={`offset-start-${i}`} className="h-9 sm:h-10 md:h-11" />
          ))}

          {days.map((day) => {
            const isPast = isBefore(day, today);
            const isSelected = selectedDate && isSameDay(day, selectedDate);
            const isTodayDate = isSameDay(day, today);
            const isSunday = getDay(day) === 0;

            return (
              <button
                key={day.toISOString()}
                type="button"
                disabled={isPast || isSunday}
                onClick={() => handleDateSelect(day)}
                className={cn(
                  "h-9 sm:h-10 md:h-11 w-full rounded-xl flex items-center justify-center text-xs sm:text-sm font-medium transition-colors relative",
                  isSelected && "bg-[var(--sahi-green)] text-white font-bold shadow-sm cursor-pointer",
                  !isSelected && !isPast && !isSunday && "hover:bg-gray-200 text-[var(--text-color)] cursor-pointer",
                  (isPast || isSunday) && "text-gray-300 cursor-default",
                  isTodayDate && !isSelected && "border border-[var(--sahi-green)] font-bold text-[var(--sahi-green)]"
                )}
              >
                {format(day, 'd')}
              </button>
            );
          })}

          {Array.from({ length: trailingOffset }).map((_, i) => (
            <div key={`offset-end-${i}`} className="h-9 sm:h-10 md:h-11" />
          ))}
        </div>
      </div>

      {/* Time & Notes Form Controls */}
      <div className="mt-3.5 sm:mt-4 space-y-2.5">
        <div>
          <label className="block text-xs font-normal text-[var(--sub-text-color)] mb-1">
            Select Time
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[
              { label: '11:00 AM', time24: '11:00' },
              { label: '3:00 PM', time24: '15:00' },
              { label: '5:30 PM', time24: '17:30' },
            ].map((slot) => {
              const isSelected = selectedTime24 === slot.time24;
              return (
                <SelectableChip
                  key={slot.time24}
                  label={slot.label}
                  isSelected={isSelected}
                  disabled={!selectedDate}
                  size="md"
                  onClick={() => handleTimeChange(slot.time24)}
                  className="w-full text-xs font-medium rounded-md whitespace-nowrap"
                />
              );
            })}
            <div className="w-full min-w-0">
              <TimeInputWithDialog
                value={selectedTime24}
                onChange={handleTimeChange}
                date={selectedDate}
                disabled={!selectedDate}
                hideLabel={true}
                placeholder="Custom"
              />
            </div>
          </div>
        </div>

        <FeedbackTextarea
          label="Discussion Topic"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Any specific topic, question, or note for the meeting..."
          rows={2}
          maxLength={400}
        />

        <SubmitButton
          onClick={handleScheduleSubmit}
          disabled={!selectedDate || !selectedSlot}
          isSubmitting={isSubmitting}
          label="Confirm & Schedule Call"
          className="mt-1"
        />
      </div>
    </div>
  );
};

// ====================================================
// SUB-VIEW 2: DECISION PULL (m=2)
// ====================================================
const DecisionPullView = ({ agencyID, threadInfo }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedDecision, setSelectedDecision] = useState('');

  // Card A state
  const [notesA, setNotesA] = useState('');

  // Card B state
  const [followUpDate, setFollowUpDate] = useState('');
  const [selectedDaysB, setSelectedDaysB] = useState(null);
  const [notesB, setNotesB] = useState('');
  const [isCalendarOpenB, setIsCalendarOpenB] = useState(false);

  // Card C state
  const [selectedReason, setSelectedReason] = useState('');
  const [competitorOrOtherName, setCompetitorOrOtherName] = useState('');
  const [notesC, setNotesC] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Rejection reasons list from threadInfo (RejRsns)
  const reasonsList = React.useMemo(() => {
    const parsed = parseRejectionReasons(threadInfo?.rejRsns, threadInfo?.typeID);
    if (parsed && parsed.length > 0) {
      return parsed;
    }
    return PRESET_NON_PURCHASE_REASONS;
  }, [threadInfo?.rejRsns, threadInfo?.typeID]);

  const handleCardBTimeframe = (days) => {
    setSelectedDaysB(days);
    const d = new Date();
    d.setDate(d.getDate() + days);
    setFollowUpDate(d.toISOString().split('T')[0]);
  };

  const handleCustomCalendarSelectB = (date) => {
    if (date) {
      setFollowUpDate(format(date, 'yyyy-MM-dd'));
      setSelectedDaysB('custom');
      setIsCalendarOpenB(false);
    }
  };

  const handleDecisionSubmit = async (e, decisionType) => {
    e?.preventDefault();
    const decision = decisionType || selectedDecision;

    if (!decision) {
      toast.error('Please select an option.');
      return;
    }

    if (decision === 'more_time' && !followUpDate) {
      toast.error('Please select when we should reconnect with you.');
      return;
    }

    if (decision === 'not_proceeding' && !selectedReason) {
      toast.error('Please select the primary reason.');
      return;
    }

    const pickedReasonObj = reasonsList.find(r => r.id === selectedReason);
    const reasonLabel = pickedReasonObj?.label || selectedReason;
    const isCompetitor =
      reasonLabel.toLowerCase().includes('competitor') ||
      reasonLabel.toLowerCase().includes('vendor');

    if (decision === 'not_proceeding' && isCompetitor && !competitorOrOtherName.trim()) {
      toast.error('Please provide details for the selected reason.');
      return;
    }

    setIsSubmitting(true);
    try {
      if (threadInfo?.threadID && agencyID) {
        let fdbkScore = 1;
        let rejReason = '';
        let combinedNotes = '';

        if (decision === 'proceed') {
          fdbkScore = 1;
          combinedNotes = `Status: Ready to Proceed${notesA ? ` | Notes: ${notesA.trim()}` : ''}`;
        } else if (decision === 'more_time') {
          fdbkScore = 2;
          rejReason = followUpDate ? `${followUpDate} 10:00:00` : '';
          combinedNotes = `Status: Delay - Need More Time | Reconnect: ${followUpDate}${notesB ? ` | Notes: ${notesB.trim()}` : ''}`;
        } else {
          fdbkScore = 3;
          rejReason = reasonLabel;
          const extraInfo = [
            competitorOrOtherName.trim() ? `Specific: ${competitorOrOtherName.trim()}` : '',
            notesC.trim() ? `Notes: ${notesC.trim()}` : ''
          ].filter(Boolean).join(' | ');
          combinedNotes = extraInfo ? `Reason: ${reasonLabel} | ${extraInfo}` : `Reason: ${reasonLabel}`;
        }

        await externalAPI.setFeedbackDetails(
          {
            agencyID: Number(agencyID),
            connID: threadInfo.connID,
            threadID: Number(threadInfo.threadID),
            fdbkScore,
            rejReason,
            fdbkNotes: combinedNotes
          },
          'DECISION'
        );
      }
      setIsSubmitting(false);
      setCurrentStep(2);
      toast.success('Your response has been recorded!');
    } catch (err) {
      console.error('Error submitting decision:', err);
      setIsSubmitting(false);
      setCurrentStep(2);
      toast.success('Your response has been recorded!');
    }
  };

  if (currentStep === 2) {
    const getConfirmationMessage = () => {
      if (selectedDecision === 'proceed') {
        return 'Our team has been notified and will reach out to you shortly.';
      }
      if (selectedDecision === 'more_time') {
        return (
          <>
            We have noted your follow-up timeline (<strong>{followUpDate || 'as requested'}</strong>). We will connect back with you then.
          </>
        );
      }
      return 'Thank you for letting us know. We appreciate your time and hope to collaborate in the future!';
    };

    return (
      <ConfirmationView
        title="Thank You!"
        message={getConfirmationMessage()}
        badgeText="We will get back to you at the earliest."
      />
    );
  }

  const customerName = threadInfo?.contactName || threadInfo?.bizName || 'Customer';
  const productName = threadInfo?.mainProduct;
  const productQty = threadInfo?.mainQty;
  const productDisplay = productName
    ? `${productName}${productQty ? ` (${productQty})` : ''}`
    : 'your requirement';

  const pickedReasonObj = reasonsList.find(r => r.id === selectedReason);
  const pickedReasonLabel = pickedReasonObj?.label || selectedReason;
  const isCompetitor =
    pickedReasonLabel.toLowerCase().includes('competitor') ||
    pickedReasonLabel.toLowerCase().includes('vendor');

  return (
    <div className="flex flex-col justify-between h-full flex-1">
      <div>
        <div className="mb-3.5 sm:mb-4 text-center">
          <h2 className="text-base sm:text-lg md:text-xl font-medium text-[var(--text-color)]">
            Inquiry Decision
          </h2>
          <p className="text-xs sm:text-sm text-[var(--sub-text-color)] mt-1 max-w-lg mx-auto [text-wrap:balance]">
            {productName ? (
              <>
                Hi <span className="font-semibold text-[var(--text-color)]">{customerName}</span>, please let us know how you would like to proceed with your requirement for <span className="font-semibold text-[var(--text-color)]">{productDisplay}</span>.
              </>
            ) : (
              <>
                Hi <span className="font-semibold text-[var(--text-color)]">{customerName}</span>, please let us know how you would like to proceed with your requirement.
              </>
            )}
          </p>
        </div>

        {/* Options List with In-Card Expansion */}
        <div className="space-y-3">
          {/* ==================================================== */}
          {/* CARD A: READY TO PROCEED */}
          {/* ==================================================== */}
          <AccordionCard
            isSelected={selectedDecision === 'proceed'}
            onSelect={() => setSelectedDecision('proceed')}
            title="Ready to Proceed"
            icon={CheckCircle2}
            iconColor="text-[var(--sahi-green)]"
          >
            {/* Quick-reply Chips */}
            <div>
              <label className="block text-xs font-normal text-[var(--sub-text-color)] mb-1.5">
                Quick Note:
              </label>
              <div className="flex flex-wrap gap-1.5">
                {[
                  'Please send proforma invoice',
                  'Call me to finalize terms',
                  'Need urgent delivery',
                  'Chat with me on WhatsApp'
                ].map((text) => (
                  <SelectableChip
                    key={text}
                    label={text}
                    isSelected={notesA === text}
                    onClick={() => setNotesA(notesA === text ? '' : text)}
                  />
                ))}
              </div>
            </div>

            {/* Additional Note input */}
            <FeedbackTextarea
              value={notesA}
              onChange={(e) => setNotesA(e.target.value)}
              placeholder="Any special instructions or note..."
              rows={2}
              maxLength={400}
            />

            <SubmitButton
              onClick={(e) => handleDecisionSubmit(e, 'proceed')}
              isSubmitting={isSubmitting}
              label="Confirm & Proceed with Order"
            />
          </AccordionCard>

          {/* ==================================================== */}
          {/* CARD B: NEED MORE TIME */}
          {/* ==================================================== */}
          <AccordionCard
            isSelected={selectedDecision === 'more_time'}
            onSelect={() => setSelectedDecision('more_time')}
            title="Need More Time"
            icon={Hourglass}
            iconColor="text-amber-600"
          >
            {/* Required Reconnect Question */}
            <div>
              <label className="block text-xs font-medium text-[var(--text-color)] mb-1.5">
                When should we reconnect with you? <span className="text-rose-500">*</span>
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                {[
                  { label: 'In 1 Week', days: 7 },
                  { label: 'In 2 Weeks', days: 14 },
                  { label: 'Next Month', days: 30 },
                ].map((opt) => (
                  <SelectableChip
                    key={opt.label}
                    label={opt.label}
                    isSelected={selectedDaysB === opt.days}
                    onClick={() => handleCardBTimeframe(opt.days)}
                    className="font-medium"
                  />
                ))}

                {/* Custom Date Button with Popover Calendar */}
                <Popover open={isCalendarOpenB} onOpenChange={setIsCalendarOpenB}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className={cn(
                        "relative h-9 px-2 text-xs font-medium rounded-lg border transition-all select-none cursor-pointer flex items-center justify-center gap-1 text-center bg-white",
                        selectedDaysB === 'custom' || (followUpDate && ![7, 14, 30].includes(selectedDaysB))
                          ? "border-[var(--sahi-green)] text-[var(--sahi-green)] shadow-xs"
                          : "border-[var(--border)] text-[var(--text-color)] hover:bg-gray-100"
                      )}
                    >
                      <CalendarIcon className="w-3.5 h-3.5 shrink-0" />
                      <span className="truncate">
                        {followUpDate && selectedDaysB === 'custom'
                          ? format(new Date(followUpDate), 'dd MMM')
                          : 'Custom Date'}
                      </span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-auto p-0 bg-white border border-gray-200 shadow-lg z-[10030]"
                    align="center"
                    sideOffset={5}
                  >
                    <Calendar
                      mode="single"
                      selected={followUpDate ? new Date(followUpDate) : undefined}
                      captionLayout="dropdown"
                      fromYear={new Date().getFullYear()}
                      toYear={new Date().getFullYear() + 2}
                      className="rdp-dropdown-custom"
                      disabled={(date) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        return date < today;
                      }}
                      onSelect={handleCustomCalendarSelectB}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Quick-reply buttons for Note */}
            <div>
              <label className="block text-xs font-normal text-[var(--sub-text-color)] mb-1.5">
                Quick Note:
              </label>
              <div className="flex flex-wrap gap-1.5">
                {[
                  'Budget approval in progress',
                  'Evaluating technical alternatives',
                  'Revisit with team next week',
                  'Final decision pending management'
                ].map((text) => (
                  <SelectableChip
                    key={text}
                    label={text}
                    isSelected={notesB === text}
                    onClick={() => setNotesB(notesB === text ? '' : text)}
                  />
                ))}
              </div>
            </div>

            {/* Additional Note input */}
            <FeedbackTextarea
              value={notesB}
              onChange={(e) => setNotesB(e.target.value)}
              placeholder="Additional context or notes..."
              rows={2}
              maxLength={400}
            />

            <SubmitButton
              onClick={(e) => handleDecisionSubmit(e, 'more_time')}
              disabled={!followUpDate}
              isSubmitting={isSubmitting}
              label="Submit Decision"
            />
          </AccordionCard>

          {/* ==================================================== */}
          {/* CARD C: NOT PROCEEDING */}
          {/* ==================================================== */}
          <AccordionCard
            isSelected={selectedDecision === 'not_proceeding'}
            onSelect={() => setSelectedDecision('not_proceeding')}
            title="Not Proceeding"
            icon={XCircle}
            iconColor="text-rose-600"
          >
            {/* Required Primary Reason Question */}
            <div>
              <label className="block text-xs font-medium text-[var(--text-color)] mb-1.5">
                Please let us know the primary reason: <span className="text-rose-500">*</span>
              </label>
              <div className="flex flex-wrap gap-1.5">
                {reasonsList.map((reason) => (
                  <SelectableChip
                    key={reason.id}
                    label={reason.label}
                    isSelected={selectedReason === reason.id}
                    onClick={() => setSelectedReason(reason.id)}
                  />
                ))}
              </div>
            </div>

            {/* If picked competitor/vendor, ask to name it */}
            {isCompetitor && (
              <div>
                <label className="block text-xs font-medium text-[var(--text-color)] mb-1">
                  Competitor / Vendor name: <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  value={competitorOrOtherName}
                  onChange={(e) => setCompetitorOrOtherName(e.target.value)}
                  placeholder="e.g. Vendor Name"
                  className="w-full text-xs sm:text-sm bg-white border border-[var(--border)] sahi-radius px-3 py-2 text-[var(--text-color)] placeholder:text-[var(--sub-text-color)] focus:outline-none focus:border-[var(--sahi-green)] focus:ring-0 block"
                />
              </div>
            )}

            {/* Additional Note input */}
            <FeedbackTextarea
              value={notesC}
              onChange={(e) => setNotesC(e.target.value)}
              placeholder="Additional details or feedback..."
              rows={2}
              maxLength={400}
            />

            <SubmitButton
              onClick={(e) => handleDecisionSubmit(e, 'not_proceeding')}
              disabled={!selectedReason || (isCompetitor && !competitorOrOtherName.trim())}
              isSubmitting={isSubmitting}
              label="Submit Decision"
            />
          </AccordionCard>
        </div>
      </div>
    </div>
  );
};

// ====================================================
// SUB-VIEW 3: NON-PURCHASE FEEDBACK (m=3)
// ====================================================
const NonPurchaseView = ({ agencyID, threadInfo }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedReason, setSelectedReason] = useState('');
  const [feedbackDetails, setFeedbackDetails] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Parse dynamic rejection reasons from API (Configs.RejRsns) for this thread type
  const reasonsList = React.useMemo(() => {
    const parsed = parseRejectionReasons(threadInfo?.rejRsns, threadInfo?.typeID);
    if (parsed && parsed.length > 0) {
      return parsed;
    }
    return PRESET_NON_PURCHASE_REASONS;
  }, [threadInfo?.rejRsns, threadInfo?.typeID]);

  const handleFeedbackSubmit = async (e) => {
    e?.preventDefault();

    if (!selectedReason) {
      toast.error('Please select a reason.');
      return;
    }

    const picked = reasonsList.find(r => r.id === selectedReason);
    const reasonLabel = picked?.label || selectedReason;
    const isOther = reasonLabel.toLowerCase().includes('other');

    if (isOther && !feedbackDetails.trim()) {
      toast.error('Please specify your reason in the feedback field.');
      return;
    }

    setIsSubmitting(true);
    try {
      if (threadInfo?.threadID && agencyID) {
        await externalAPI.setFeedbackDetails(
          {
            agencyID: Number(agencyID),
            connID: threadInfo.connID,
            threadID: Number(threadInfo.threadID),
            fdbkScore: 3,
            rejReason: reasonLabel,
            fdbkNotes: feedbackDetails.trim()
          },
          'DECISION'
        );
      }
      setIsSubmitting(false);
      setCurrentStep(2);
      toast.success('Feedback submitted successfully!');
    } catch (err) {
      console.error('Error submitting non-purchase feedback:', err);
      setIsSubmitting(false);
      setCurrentStep(2);
      toast.success('Feedback recorded!');
    }
  };

  if (currentStep === 2) {
    return (
      <ConfirmationView
        title="Thank You!"
        message="Your feedback has been submitted successfully. It helps us continually improve our product and offerings."
      />
    );
  }

  const selectedReasonObj = reasonsList.find(r => r.id === selectedReason);
  const isSelectedOther = selectedReasonObj?.label?.toLowerCase().includes('other');

  const customerName = threadInfo?.contactName || threadInfo?.bizName || 'Customer';

  return (
    <div className="flex flex-col justify-between h-full flex-1">
      <div>
        <div className="mb-3.5 sm:mb-4 text-center">
          <h2 className="text-base sm:text-lg md:text-xl font-medium text-[var(--text-color)]">
            Help Us Improve
          </h2>
          <p className="text-xs sm:text-sm text-[var(--sub-text-color)] mt-1 max-w-lg mx-auto [text-wrap:balance]">
            Hi <span className="font-semibold text-[var(--text-color)]">{customerName}</span>, we'd love to know why you decided not to proceed.
          </p>
        </div>

        {/* Reason Selectable Options from API (RejRsns) in Grid like m=3 */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-2.5">
          {reasonsList.map((reason) => (
            <SelectableChip
              key={reason.id}
              label={reason.label}
              isSelected={selectedReason === reason.id}
              size="md"
              showCheckmark={true}
              onClick={() => setSelectedReason(reason.id)}
            />
          ))}
        </div>

        {/* Detailed Feedback Textarea */}
        <div className="pt-5 sm:pt-6 pb-1 sm:pb-2">
          <FeedbackTextarea
            value={feedbackDetails}
            onChange={(e) => setFeedbackDetails(e.target.value)}
            placeholder="Additional feedback or thoughts..."
            rows={2}
            maxLength={500}
            className="p-3"
          />
        </div>
      </div>

      <div className="pt-4 sm:pt-5">
        <SubmitButton
          onClick={handleFeedbackSubmit}
          disabled={!selectedReason || (selectedReason === 'other' && !feedbackDetails.trim())}
          isSubmitting={isSubmitting}
          label="Submit Feedback"
        />
      </div>
    </div>
  );
};

// ====================================================
// SUB-VIEW 4: LEAD QUALIFICATION (m=4)
// ====================================================
const LeadQualificationView = ({ agencyID, threadInfo }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedUrgency, setSelectedUrgency] = useState('');
  const [notes, setNotes] = useState('');

  // Not Proceeding inline state in m=2 (using RejRsns)
  const [selectedReason, setSelectedReason] = useState('');
  const [competitorOrOtherName, setCompetitorOrOtherName] = useState('');
  const [notesNotProceeding, setNotesNotProceeding] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Parse RejRsns for LeadQualificationView Not Proceeding
  const rejReasonsList = React.useMemo(() => {
    const parsed = parseRejectionReasons(threadInfo?.rejRsns, threadInfo?.typeID);
    if (parsed && parsed.length > 0) {
      return parsed;
    }
    return PRESET_NON_PURCHASE_REASONS;
  }, [threadInfo?.rejRsns, threadInfo?.typeID]);

  const pickedReasonObj = rejReasonsList.find(r => r.id === selectedReason);
  const pickedReasonLabel = pickedReasonObj?.label || selectedReason;
  const isCompetitor =
    pickedReasonLabel.toLowerCase().includes('competitor') ||
    pickedReasonLabel.toLowerCase().includes('vendor');

  const handleQualifySubmit = async (e, forcedType) => {
    e?.preventDefault();
    const activeUrgency = forcedType || selectedUrgency;

    if (!activeUrgency) {
      toast.error('Please select an option to proceed.');
      return;
    }

    if (activeUrgency === 'not_proceeding') {
      if (!selectedReason) {
        toast.error('Please select the primary reason.');
        return;
      }
      if (isCompetitor && !competitorOrOtherName.trim()) {
        toast.error('Please provide details for the selected reason.');
        return;
      }
    }

    setIsSubmitting(true);
    try {
      if (threadInfo?.threadID && agencyID) {
        const pickedObj = BUYING_URGENCY_OPTIONS.find((opt) => opt.id === activeUrgency);
        const urgencyTitle = pickedObj?.title || activeUrgency;
        const isNotProceeding = activeUrgency === 'not_proceeding' || activeUrgency === 'not_interested';

        let fdbkScore = 2;
        let rejReason = '';
        let combinedNotes = '';

        if (isNotProceeding) {
          fdbkScore = 3;
          rejReason = pickedReasonLabel;
          const extraInfo = [
            competitorOrOtherName.trim() ? `Specific: ${competitorOrOtherName.trim()}` : '',
            notes.trim() ? `Notes: ${notes.trim()}` : ''
          ].filter(Boolean).join(' | ');
          combinedNotes = extraInfo ? `Status: Not Proceeding | Reason: ${pickedReasonLabel} | ${extraInfo}` : `Status: Not Proceeding | Reason: ${pickedReasonLabel}`;
        } else {
          fdbkScore = activeUrgency === 'immediate' ? 1 : 2;
          combinedNotes = `Status: ${urgencyTitle}${notes ? ` | Notes: ${notes.trim()}` : ''}`;
        }

        await externalAPI.setFeedbackDetails(
          {
            agencyID: Number(agencyID),
            connID: threadInfo.connID,
            threadID: Number(threadInfo.threadID),
            fdbkScore,
            rejReason,
            fdbkNotes: combinedNotes
          },
          'QUALIFY'
        );
      }
      setIsSubmitting(false);
      setCurrentStep(2);
      toast.success('Qualification details submitted!');
    } catch (err) {
      console.error('Error submitting qualification:', err);
      setIsSubmitting(false);
      setCurrentStep(2);
      toast.success('Details recorded!');
    }
  };

  if (currentStep === 2) {
    return (
      <ConfirmationView
        title="Thank You!"
        message="Your inquiry verification details have been recorded. Our team will review them and proceed accordingly."
        badgeText="Our sales representative has been notified."
      />
    );
  }

  const customerName = threadInfo?.contactName || threadInfo?.bizName || 'Customer';
  const standardOptions = BUYING_URGENCY_OPTIONS.filter((opt) => opt.id !== 'not_proceeding');

  return (
    <div className="flex flex-col justify-between h-full flex-1">
      <div>
        {/* Header */}
        <div className="mb-3.5 sm:mb-4 text-center">
          <h2 className="text-base sm:text-lg md:text-xl font-medium text-[var(--text-color)]">
            Inquiry Verification
          </h2>
          <p className="text-xs sm:text-sm text-[var(--sub-text-color)] mt-1 max-w-lg mx-auto [text-wrap:balance]">
            Hi <span className="font-semibold text-[var(--text-color)]">{customerName}</span>, please let us know your timeframe so we can assist you promptly.
          </p>
        </div>

        {/* Option Cards with In-Card Expansion */}
        <div className="space-y-2.5">
          {standardOptions.map((option) => (
            <AccordionCard
              key={option.id}
              isSelected={selectedUrgency === option.id}
              onSelect={() => setSelectedUrgency(option.id)}
              title={option.title}
              icon={option.icon}
              iconColor={option.iconColor}
            >
              <FeedbackTextarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Additional feedback or thoughts..."
                rows={2}
                maxLength={400}
              />

              <SubmitButton
                onClick={(e) => handleQualifySubmit(e, option.id)}
                isSubmitting={isSubmitting}
                label="Confirm & Submit"
              />
            </AccordionCard>
          ))}

          {/* Not Proceeding Card with In-Card Expansion using RejRsns */}
          <AccordionCard
            isSelected={selectedUrgency === 'not_proceeding'}
            onSelect={() => setSelectedUrgency('not_proceeding')}
            title="Not Proceeding"
            icon={XCircle}
            iconColor="text-rose-600"
          >
            {/* Required Primary Reason Question using RejRsns */}
            <div>
              <label className="block text-xs font-medium text-[var(--text-color)] mb-1.5">
                Please let us know the primary reason: <span className="text-rose-500">*</span>
              </label>
              <div className="flex flex-wrap gap-1.5">
                {rejReasonsList.map((reason) => (
                  <SelectableChip
                    key={reason.id}
                    label={reason.label}
                    isSelected={selectedReason === reason.id}
                    onClick={() => setSelectedReason(reason.id)}
                  />
                ))}
              </div>
            </div>

            {/* Additional Note input */}
            <FeedbackTextarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional details or feedback..."
              rows={2}
              maxLength={400}
            />

            <SubmitButton
              onClick={(e) => handleQualifySubmit(e, 'not_proceeding')}
              disabled={!selectedReason}
              isSubmitting={isSubmitting}
              label="Confirm & Submit"
            />
          </AccordionCard>
        </div>
      </div>
    </div>
  );
};

// ====================================================
// SUB-VIEW: NEXT SALE EXPECTATION (m=5)
// ====================================================
const NextSaleView = ({ agencyID, connectionID, threadInfo }) => {
  const [selectedDays, setSelectedDays] = useState(null);
  const [customDate, setCustomDate] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  // Compute selected effective date string (YYYY-MM-DD)
  const effectiveDate = customDate
    ? customDate
    : selectedDays
      ? format(new Date(Date.now() + selectedDays * 86400000), 'yyyy-MM-dd')
      : '';

  const handleChipSelect = (days) => {
    setSelectedDays(days);
    setCustomDate(''); // Reset custom date when picking a quick chip
  };

  const handleCustomCalendarSelect = (date) => {
    if (date) {
      setCustomDate(format(date, 'yyyy-MM-dd'));
      setSelectedDays(null);
      setIsCalendarOpen(false);
    }
  };

  const handleNextSaleSubmit = async () => {
    if (!effectiveDate) {
      toast.error('Please select an expected repurchase timeline.');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        agencyID: Number(agencyID),
        connID: Number(connectionID),
        type: 'NEXTSALE',
        fdbkScore: 1,
        fdbkNotes: JSON.stringify({
          ExpectedDT: effectiveDate,
          Note: notes.trim()
        })
      };

      const res = await externalAPI.setConnectionFeedback(payload);
      if (res?.error === false || res?.message === 'Success') {
        setIsSubmitted(true);
      } else {
        toast.error(res?.message || 'Could not save expected sale details.');
      }
    } catch (err) {
      console.error('Error submitting next sale:', err);
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <ConfirmationView
        title="Thank You!"
        message="Your next order timeline has been noted. We will stay in touch and connect with you ahead of time."
        badgeText="Our relationship executive has been notified."
      />
    );
  }

  const customerName = threadInfo?.contactName || threadInfo?.bizName || 'Customer';

  return (
    <div className="flex flex-col justify-between h-full flex-1">
      <div>
        {/* Header */}
        <div className="mb-3.5 sm:mb-4 text-center">
          <h2 className="text-base sm:text-lg md:text-xl font-medium text-[var(--text-color)]">
            Next Requirement Planning
          </h2>
          <p className="text-xs sm:text-sm text-[var(--sub-text-color)] mt-1 max-w-lg mx-auto [text-wrap:balance]">
            Hi <span className="font-semibold text-[var(--text-color)]">{customerName}</span>, when do you expect your next requirement or repeat order?
          </p>
        </div>

        {/* Quick Chips Selection (2 per row on mobile, 3 per row on sm+) */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-2.5">
          {NEXT_SALE_CHIPS.map((chip) => {
            const isSelected = selectedDays === chip.days && !customDate;
            return (
              <SelectableChip
                key={chip.label}
                label={chip.label}
                isSelected={isSelected}
                size="md"
                showCheckmark={true}
                onClick={() => handleChipSelect(chip.days)}
              />
            );
          })}

          {/* 6th Chip: Custom Date with Popover Calendar */}
          <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className={cn(
                  "relative h-11 px-3 sm:px-3.5 rounded-xl border transition-all flex items-center justify-center gap-1.5 text-xs sm:text-sm select-none cursor-pointer active:scale-[0.98] font-normal bg-white",
                  customDate
                    ? "border-[var(--sahi-green)] text-[var(--sahi-green)] shadow-xs"
                    : "border-[var(--border)] text-[var(--text-color)] hover:bg-gray-100/80"
                )}
              >
                {customDate ? (
                  <Check className="w-4 h-4 stroke-[2.5] text-[var(--sahi-green)] shrink-0" />
                ) : (
                  <CalendarIcon className="w-4 h-4 text-[var(--sub-text-color)] shrink-0" />
                )}
                <span className="truncate leading-none">
                  {customDate ? format(new Date(customDate), 'dd MMM yyyy') : 'Custom'}
                </span>
              </button>
            </PopoverTrigger>
            <PopoverContent
              className="w-auto p-0 bg-white border border-gray-200 shadow-lg z-[10030]"
              align="end"
              sideOffset={5}
            >
              <Calendar
                mode="single"
                selected={customDate ? new Date(customDate) : undefined}
                captionLayout="dropdown"
                fromYear={new Date().getFullYear()}
                toYear={new Date().getFullYear() + 2}
                className="rdp-dropdown-custom"
                disabled={(date) => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  return date < today;
                }}
                onSelect={handleCustomCalendarSelect}
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Additional Notes Textarea */}
        <div className="pt-3.5 sm:pt-4 pb-1">
          <FeedbackTextarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Items needed, estimated volume, or any specific remarks..."
            rows={2}
            maxLength={400}
            className="p-3"
          />
        </div>
      </div>

      <div className="pt-4 sm:pt-5">
        <SubmitButton
          onClick={handleNextSaleSubmit}
          disabled={!effectiveDate}
          isSubmitting={isSubmitting}
          label="Save Next Order Timeline"
        />
      </div>
    </div>
  );
};

// ====================================================
// SUB-VIEW 4: CUSTOMER FEEDBACK (m=4)
// ====================================================
const CustomerFeedbackView = ({ agencyID, threadInfo, initialRating = 0 }) => {
  const [rating, setRating] = useState(Number(initialRating) || 0);
  const [hoverRating, setHoverRating] = useState(0);
  const [selectedReason, setSelectedReason] = useState('');
  const [feedbackNotes, setFeedbackNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [hasExistingFeedback, setHasExistingFeedback] = useState(false);

  const currencySymbol = useCurrency(threadInfo?.countryID || null);

  // Parse failure / rejection reasons for low ratings (1-3 stars)
  const failureReasonsList = React.useMemo(() => {
    const parsed = parseRejectionReasons(threadInfo?.rejRsns, threadInfo?.typeID);
    if (parsed && parsed.length > 0) {
      return parsed;
    }
    return PRESET_NON_PURCHASE_REASONS;
  }, [threadInfo?.rejRsns, threadInfo?.typeID]);

  // Sync pre-existing feedback from threadInfo (when opening previously submitted feedback)
  useEffect(() => {
    if (threadInfo?.fdbkScore && Number(threadInfo.fdbkScore) > 0) {
      const score = Number(threadInfo.fdbkScore);
      setRating(score);
      setHasExistingFeedback(true);
      setIsSubmitted(true);
    }
    if (threadInfo?.fdbkNotes) {
      setFeedbackNotes(threadInfo.fdbkNotes);
    }
    if (threadInfo?.rejReason) {
      setSelectedReason(threadInfo.rejReason);
    }
  }, [threadInfo]);

  const isLowRating = rating > 0 && rating <= 3;
  const activeRatingObj = RATINGS_FEEDBACK_CONFIG.find((r) => r.score === (hoverRating || rating));

  const handleStarClick = (score) => {
    if (hasExistingFeedback) return;
    setRating(score);
    if (score > 3) {
      setSelectedReason('');
    }
  };

  const handleFeedbackSubmit = async (e) => {
    e?.preventDefault();

    if (rating === 0) {
      toast.error('Please select a star rating.');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        agencyID: Number(agencyID),
        connID: Number(threadInfo?.connID || 0),
        threadID: Number(threadInfo?.threadID || 0),
        fdbkScore: rating,
        fdbkNotes: feedbackNotes.trim(),
        rejReason: isLowRating ? selectedReason : '',
        type: 'FDBK'
      };

      const result = await externalAPI.setFeedbackDetails(payload, 'FDBK');

      if (result?.error === false || result?.message === 'Success' || !result?.HasError) {
        setHasExistingFeedback(true);
        setIsSubmitted(true);
        toast.success('Thank you! Your feedback has been submitted.');
      } else {
        toast.error(result?.message || 'Could not submit feedback. Please try again.');
      }
    } catch (err) {
      console.error('Error submitting customer feedback:', err);
      toast.error('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    const isHighRating = rating === 4 || rating === 5;
    const googleRevLink = threadInfo?.googleLink || '';

    return (
      <ConfirmationView
        title="Thank You!"
        message="Your feedback has been submitted successfully. It helps us continually improve our products and service standards."
        badgeText={rating ? `Rated ${rating} Star${rating > 1 ? 's' : ''}` : undefined}
      >
        {isHighRating && googleRevLink && (
          <div className="w-full mt-5 p-4 rounded-xl border border-[var(--border)] bg-gray-50/70 flex flex-col sm:flex-row items-center justify-between gap-3 text-left">
            <div className="text-xs sm:text-sm text-[var(--text-color)]">
              Would you like to share your review on <span className="font-semibold text-zinc-900">Google Reviews</span>?
            </div>
            <button
              type="button"
              onClick={() => window.open(googleRevLink, '_blank')}
              className="w-full sm:w-auto h-9 px-3.5 bg-[var(--sahi-green)] text-white hover:bg-white hover:text-[var(--sahi-green)] border border-[var(--sahi-green)] rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 shadow-xs transition-colors shrink-0"
            >
              Google Reviews
              <ExternalLink className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </ConfirmationView>
    );
  }

  const customerName = threadInfo?.contactName || threadInfo?.bizName || 'Customer';
  const orderIdentifier = threadInfo?.reference || (threadInfo?.threadID ? `#${threadInfo.threadID}` : '');

  return (
    <div className="flex flex-col justify-between h-full flex-1">
      <div>
        {/* Header */}
        <div className="mb-4 sm:mb-5 text-center">
          <h2 className="text-base sm:text-lg md:text-xl font-medium text-[var(--text-color)]">
            Feedback
          </h2>
          <p className="text-xs sm:text-sm text-[var(--sub-text-color)] mt-1.5 mx-auto [text-wrap:balance]">
            Hi <span className="font-semibold text-[var(--text-color)]">{customerName}</span>, please help us become your favourite supplier!
          </p>
        </div>

        {/* Order / Inquiry Summary Details if present */}
        {(threadInfo?.mainProduct || threadInfo?.totalAmount || threadInfo?.mainQty || threadInfo?.sinceDT || threadInfo?.doneDT) && (
          <div className="mb-5 sm:mb-6">
            <div className="grid grid-cols-2 gap-2 sm:gap-2.5">
              {threadInfo?.mainProduct && (
                <div className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-md bg-gray-100 text-xs sm:text-sm text-[var(--sub-text-color)] min-w-0">
                  <span className="shrink-0 text-gray-500">Offering:</span>
                  <span className="font-semibold text-zinc-900 truncate">{threadInfo.mainProduct}</span>
                </div>
              )}
              {threadInfo?.sinceDT && (
                <div className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-md bg-gray-100 text-xs sm:text-sm text-[var(--sub-text-color)] min-w-0">
                  <span className="shrink-0 text-gray-500">Order Placed:</span>
                  <span className="font-semibold text-zinc-900 truncate">{format(new Date(threadInfo.sinceDT), 'dd-MMM')}</span>
                </div>
              )}
              {threadInfo?.totalAmount && (
                <div className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-md bg-gray-100 text-xs sm:text-sm text-[var(--sub-text-color)] min-w-0">
                  <span className="shrink-0 text-gray-500">Value:</span>
                  <span className="font-semibold text-zinc-900 truncate">{currencySymbol} {threadInfo.totalAmount}</span>
                </div>
              )}
              {threadInfo?.mainQty && (
                <div className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-md bg-gray-100 text-xs sm:text-sm text-[var(--sub-text-color)] min-w-0">
                  <span className="shrink-0 text-gray-500">Qty:</span>
                  <span className="font-semibold text-zinc-900 truncate">{threadInfo.mainQty}</span>
                </div>
              )}
              {threadInfo?.doneDT && (
                <div className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-md bg-gray-100 text-xs sm:text-sm text-[var(--sub-text-color)] min-w-0">
                  <span className="shrink-0 text-gray-500">Completed On:</span>
                  <span className="font-semibold text-zinc-900 truncate">{format(new Date(threadInfo.doneDT), 'dd-MMM')}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Interactive Star Rating Section */}
        <div className="space-y-2.5 mb-5 sm:mb-6">
          <label className="block text-sm sm:text-base font-medium text-[var(--text-color)] text-left">
            How do you feel about working with us?
          </label>

          <div className="flex items-center justify-start gap-3 sm:gap-4 py-0.5">
            <div className="flex space-x-1 sm:space-x-1.5 -ml-1">
              {[1, 2, 3, 4, 5].map((starIndex) => {
                const currentScore = hoverRating || rating;
                const activeColor = RATINGS_FEEDBACK_CONFIG[currentScore - 1]?.color;

                return (
                  <button
                    key={starIndex}
                    type="button"
                    onClick={() => handleStarClick(starIndex)}
                    onMouseEnter={() => !hasExistingFeedback && setHoverRating(starIndex)}
                    onMouseLeave={() => !hasExistingFeedback && setHoverRating(0)}
                    disabled={isSubmitting || hasExistingFeedback}
                    className="transition-colors duration-200 focus:outline-none"
                  >
                    <Star
                      fill="currentColor"
                      className={`
                        h-8 w-8 sm:h-10 sm:w-10
                        ${starIndex <= currentScore
                          ? `fill-${activeColor} text-${activeColor}`
                          : 'text-gray-300'
                        }
                        hover:scale-110 transition-transform duration-200
                        ${(isSubmitting || hasExistingFeedback) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                      `}
                    />
                  </button>
                );
              })}
            </div>

            {/* Show selected rating label */}
            {(rating > 0 || hoverRating > 0) && (
              <div>
                <span className={`text-sm sm:text-base md:text-lg font-medium text-${RATINGS_FEEDBACK_CONFIG[(hoverRating || rating) - 1]?.color}`}>
                  {RATINGS_FEEDBACK_CONFIG[(hoverRating || rating) - 1]?.label}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Dynamic Rejection / Failure Reason Chips for low ratings (1-3 stars) */}
        {isLowRating && failureReasonsList.length > 0 && (
          <div className="space-y-2 mb-5 sm:mb-6 animate-in fade-in duration-200">
            <label className="block text-xs sm:text-sm font-medium text-[var(--text-color)] text-left">
              Please let us know what went wrong:
            </label>
            <div className="flex flex-wrap gap-1.5 sm:gap-2">
              {failureReasonsList.map((reason) => (
                <SelectableChip
                  key={reason.id}
                  label={reason.label}
                  isSelected={selectedReason === reason.label || selectedReason === reason.id}
                  onClick={() => setSelectedReason(prev => (prev === (reason.label || reason.id) ? '' : (reason.label || reason.id)))}
                />
              ))}
            </div>
          </div>
        )}

        {/* Additional Feedback Textarea */}
        <div className="mb-2">
          <FeedbackTextarea
            value={feedbackNotes}
            onChange={(e) => setFeedbackNotes(e.target.value)}
            placeholder="Enter your feedback or suggestions..."
            rows={3}
            maxLength={500}
            className="p-3 text-xs sm:text-sm rounded-lg"
          />
        </div>
      </div>

      <div className="pt-5 sm:pt-6">
        <SubmitButton
          onClick={handleFeedbackSubmit}
          disabled={rating === 0}
          isSubmitting={isSubmitting}
          label="Submit Feedback"
        />
      </div>
    </div>
  );
};

// ====================================================
// MAIN PAGE COMPONENT: Connect2
// ====================================================
const Connect2 = () => {
  const location = useLocation();

  // Read URL parameters with aliases
  const searchParams = new URLSearchParams(location.search);
  const agencyID = searchParams.get('aid') || searchParams.get('a');
  const threadID = searchParams.get('tid') || searchParams.get('t');
  const connectionID = searchParams.get('cid') || searchParams.get('c');
  const ratingParam = searchParams.get('rating') || searchParams.get('r');
  const m = searchParams.get('m');

  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [agencyInfo, setAgencyInfo] = useState({
    agencyName: 'Sahi CRM',
    agencyTagline: 'Smart CRM & Customer Engagement Platform',
    logo: ''
  });

  const [threadInfo, setThreadInfo] = useState({
    threadID: threadID ? Number(threadID) : null,
    connID: connectionID ? Number(connectionID) : null,
    reference: '',
    mainProduct: '',
    mainQty: '',
    contactName: '',
    bizName: '',
    pocUserName: '',
    pocUserMobile: ''
  });

  // Mode-specific parameter validation:
  // - 1: Decision Pull (requires aid and tid)
  // - 2: Lead Qualification (requires aid and tid)
  // - 3: Next Sale Expectation (requires aid and cid)
  // - 4: Customer Feedback (requires aid and tid)
  // - 5: Non-Purchase Feedback (requires aid and tid)
  // - 6: Schedule (requires aid and cid or tid)
  const missingParams = [];
  if (!agencyID) missingParams.push('Agency ID (aid)');
  if (!m || !['1', '2', '3', '4', '5', '6'].includes(m)) {
    missingParams.push('Valid Mode (m=1, 2, 3, 4, 5, or 6)');
  } else if (m === '3') {
    if (!connectionID) missingParams.push('Connection ID (cid)');
  } else if (m === '6') {
    if (!threadID && !connectionID) missingParams.push('Inquiry ID (tid) or Connection ID (cid)');
  } else {
    if (!threadID) missingParams.push('Inquiry ID (tid)');
  }

  const hasMissingParams = missingParams.length > 0;

  useEffect(() => {
    if (hasMissingParams) {
      setIsLoading(false);
      return;
    }

    const fetchLiveContext = async () => {
      setIsLoading(true);
      setLoadError(null);

      try {
        if (m === '3') {
          // For Next Sale (m=3): Fetch connection & agency details via getConnectionFeedback ('NEXTSALE')
          const connRes = await externalAPI.getConnectionFeedback(
            { agencyID: Number(agencyID), connID: Number(connectionID) },
            'NEXTSALE'
          );

          if (connRes?.parsedDataSet) {
            const { Table1, Table2 } = connRes.parsedDataSet;
            const agn = Table1?.[0] || {};
            const conn = Table2?.[0] || {};

            const rawLogo = agn.Logo || agn.LogoPath || agn.logo || '';
            const resolvedLogoUrl = rawLogo
              ? (rawLogo.startsWith('http') ? rawLogo : getUploadUrl(rawLogo.startsWith('Uploads/') ? rawLogo : `Uploads/${rawLogo}`))
              : '';

            setAgencyInfo({
              agencyName: agn.AgencyName || 'Sahi CRM',
              agencyTagline: agn.Tagline || 'Smart CRM & Customer Engagement Platform',
              logo: resolvedLogoUrl
            });

            setThreadInfo({
              threadID: null,
              connID: Number(connectionID),
              reference: '',
              mainProduct: '',
              mainQty: '',
              contactName: conn.ContactName || '',
              bizName: conn.BizName || '',
              pocUserName: conn.POCUserName || '',
              pocUserMobile: conn.POCUserMobile || ''
            });
          }
        } else if (m === '6' && threadID) {
          // For Schedule (m=6) with Inquiry ID: fetch full details via getFeedbackDetails('SCHEDULE')
          const response = await externalAPI.getFeedbackDetails(
            { agencyID: Number(agencyID), threadID: Number(threadID) },
            'SCHEDULE'
          );

          if (response?.parsedDataSet) {
            const { Table1, Table2 } = response.parsedDataSet;
            const agn = Table1?.[0] || {};
            const thr = Table2?.[0] || {};

            const rawLogo = agn.Logo || agn.LogoPath || agn.logo || '';
            const resolvedLogoUrl = rawLogo
              ? (rawLogo.startsWith('http') ? rawLogo : getUploadUrl(rawLogo.startsWith('Uploads/') ? rawLogo : `Uploads/${rawLogo}`))
              : '';

            setAgencyInfo({
              agencyName: agn.AgencyName || 'Sahi CRM',
              agencyTagline: agn.Tagline || 'Smart CRM & Customer Engagement Platform',
              logo: resolvedLogoUrl
            });

            if (thr?.ThreadID) {
              setThreadInfo({
                threadID: thr.ThreadID,
                typeID: thr.TypeID || 1,
                connID: thr.ConnID || connectionID,
                reference: thr.Reference || `#${thr.ThreadID}`,
                mainProduct: thr.MainProduct ? String(thr.MainProduct).trim() : '',
                mainQty: thr.MainQty ? String(thr.MainQty).trim() : '',
                contactName: thr.ContactName || '',
                bizName: thr.BizName || '',
                pocUserName: thr.POCUserName || '',
                pocUserMobile: thr.POCUserMobile || '',
                rejRsns: agn.RejRsns || ''
              });
            }
          }
        } else if (m === '6') {
          // For Schedule (m=6) with Connection ID: Fetch agency vitals/info
          const vitalsRes = await externalAPI.getAgencyVitals(Number(agencyID));
          if (vitalsRes?.parsedDataSet?.Table1?.[0]) {
            const agn = vitalsRes.parsedDataSet.Table1[0];
            const rawLogo = agn.Logo || agn.LogoPath || agn.logo || '';
            const resolvedLogoUrl = rawLogo
              ? (rawLogo.startsWith('http') ? rawLogo : getUploadUrl(rawLogo.startsWith('Uploads/') ? rawLogo : `Uploads/${rawLogo}`))
              : '';
            setAgencyInfo({
              agencyName: agn.AgencyName || 'Sahi CRM',
              agencyTagline: agn.Tagline || 'Smart CRM & Customer Engagement Platform',
              logo: resolvedLogoUrl
            });
          }
        } else if (threadID) {
          // For m=1 (Decision Pull), m=2 (Lead Qualify), m=4 (Customer Feedback), m=5 (Non-Purchase Feedback)
          const apiType = m === '2' ? 'QUALIFY' : m === '4' ? 'FDBK' : 'DECISION';
          const response = await externalAPI.getFeedbackDetails(
            { agencyID: Number(agencyID), threadID: Number(threadID) },
            apiType
          );

          if (response?.parsedDataSet) {
            const { Table1, Table2 } = response.parsedDataSet;
            const agn = Table1?.[0] || {};
            const thr = Table2?.[0] || {};

            const rawLogo = agn.Logo || agn.LogoPath || agn.logo || '';
            const resolvedLogoUrl = rawLogo
              ? (rawLogo.startsWith('http') ? rawLogo : getUploadUrl(rawLogo.startsWith('Uploads/') ? rawLogo : `Uploads/${rawLogo}`))
              : '';

            setAgencyInfo({
              agencyName: agn.AgencyName || 'Sahi CRM',
              agencyTagline: agn.Tagline || 'Smart CRM & Customer Engagement Platform',
              logo: resolvedLogoUrl
            });

            let parsedThreadTypes = [];
            if (agn?.ThreadTypes) {
              try {
                parsedThreadTypes = typeof agn.ThreadTypes === 'string' ? JSON.parse(agn.ThreadTypes) : agn.ThreadTypes;
              } catch (e) {
                console.warn('Failed to parse ThreadTypes in Connect2', e);
              }
            }

            if (thr?.ThreadID) {
              setThreadInfo({
                threadID: thr.ThreadID,
                typeID: thr.TypeID || 1,
                connID: thr.ConnID || connectionID,
                reference: thr.Reference || `#${thr.ThreadID}`,
                mainProduct: thr.MainProduct ? String(thr.MainProduct).trim() : '',
                mainQty: thr.MainQty ? String(thr.MainQty).trim() : '',
                totalAmount: thr.BizProspect ? Number(thr.BizProspect).toLocaleString() : null,
                sinceDT: thr.SinceDT || null,
                doneDT: thr.DoneDT || null,
                contactName: thr.ContactName || '',
                bizName: thr.BizName || '',
                pocUserName: thr.POCUserName || '',
                pocUserMobile: thr.POCUserMobile || '',
                rejRsns: agn.RejRsns || '',
                googleLink: agn.GoogleLink || '',
                countryID: agn.CountryID || null,
                threadTypes: parsedThreadTypes,
                fdbkScore: thr.FdbkScore || 0,
                fdbkNotes: thr.FdbkNotes || '',
                rejReason: thr.RejRsnID || thr.FailureReason || ''
              });
            }
          } else {
            setAgencyInfo(prev => ({ ...prev, agencyName: 'Sahi CRM' }));
          }
        }
      } catch (err) {
        console.warn('Could not fetch external details, falling back to defaults:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLiveContext();
  }, [agencyID, threadID, connectionID, m, hasMissingParams]);

  // Missing Parameters Warning View
  if (hasMissingParams) {
    return (
      <ConnectLayout agencyName="Sahi CRM" agencyTagline="Smart CRM & Customer Engagement Platform">
        <div className="flex flex-col items-center justify-center py-16 text-center my-auto">
          <div className="w-12 h-12 rounded-full bg-rose-50 border border-rose-200 flex items-center justify-center text-rose-500 mb-3.5">
            <AlertCircle className="w-6 h-6 stroke-[2]" />
          </div>
          <h2 className="text-base sm:text-lg font-semibold text-[var(--text-color)] mb-1">
            Missing Parameters
          </h2>
          <p className="text-xs sm:text-sm text-[var(--sub-text-color)] max-w-sm mb-3">
            The link is incomplete or missing necessary parameters:
          </p>
          <div className="inline-flex flex-wrap gap-1.5 justify-center max-w-xs">
            {missingParams.map((param) => (
              <span key={param} className="px-2.5 py-1 text-xs sahi-radius bg-rose-50 border border-rose-200 text-rose-700 font-medium">
                {param}
              </span>
            ))}
          </div>
        </div>
      </ConnectLayout>
    );
  }

  // Loading View
  if (isLoading) {
    return (
      <ConnectLayout agencyName={agencyInfo.agencyName} agencyTagline={agencyInfo.agencyTagline}>
        <div className="flex flex-col items-center justify-center py-20 text-center my-auto">
          <div className="w-8 h-8 border-2 border-zinc-200 border-t-[var(--sahi-green)] rounded-full animate-spin mb-3" />
          <div className="text-sm font-semibold text-[var(--text-color)]">Loading details...</div>
          <div className="text-xs text-[var(--sub-text-color)] mt-0.5">Please wait a moment.</div>
        </div>
      </ConnectLayout>
    );
  }

  // Error View
  if (loadError) {
    return (
      <ConnectLayout agencyName={agencyInfo.agencyName} agencyTagline={agencyInfo.agencyTagline}>
        <div className="flex flex-col items-center justify-center py-16 text-center my-auto">
          <AlertCircle className="w-10 h-10 text-rose-500 mb-2" />
          <div className="text-sm font-semibold text-[var(--text-color)]">{loadError}</div>
        </div>
      </ConnectLayout>
    );
  }

  // Render Sub-view according to ordered modes:
  // 1 -> Decision Pull (requires aid and tid)
  // 2 -> Lead Qualification (requires aid and tid)
  // 3 -> Next Sale Expectation (requires aid and cid)
  // 4 -> Customer Feedback (requires aid and tid)
  // 5 -> Non-Purchase Feedback (requires aid and tid)
  // 6 -> Schedule (requires aid and cid or tid)
  return (
    <ConnectLayout
      agencyName={agencyInfo.agencyName}
      agencyTagline={agencyInfo.agencyTagline}
      logo={agencyInfo.logo}
      threadInfo={threadInfo}
    >
      {m === '1' && <DecisionPullView agencyID={agencyID} threadInfo={threadInfo} />}
      {m === '2' && <LeadQualificationView agencyID={agencyID} threadInfo={threadInfo} />}
      {m === '3' && <NextSaleView agencyID={agencyID} connectionID={connectionID} threadInfo={threadInfo} />}
      {m === '4' && <CustomerFeedbackView agencyID={agencyID} threadInfo={threadInfo} initialRating={ratingParam} />}
      {m === '5' && <NonPurchaseView agencyID={agencyID} threadInfo={threadInfo} />}
      {m === '6' && <ScheduleView agencyID={agencyID} connectionID={connectionID} threadInfo={threadInfo} />}
    </ConnectLayout>
  );
};

export default Connect2;
